import { useState, useEffect } from 'react';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';

// shadcn UI components
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Avatar, AvatarFallback } from '../../components/ui/avatar';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { Badge } from '../../components/ui/badge';
import { useToast } from '../../components/ui/use-toast';

// icons
import { Eye, EyeSlash, User, Lock, Sms, ShieldSecurity } from 'iconsax-react';

// project imports
import { useAuth } from '../../hooks/useAuth';
import { getProfile } from '../../services/authApi';
import { useDispatch } from 'react-redux';
import { updateProfileSuccess } from '../../store/reducers/auth';

// validation schemas
const profileSchema = Yup.object({
  name: Yup.string().required('Full name is required'),
  email: Yup.string().email('Invalid email').required('Email is required')
});

const changePasswordSchema = Yup.object({
  currentPassword: Yup.string().required('Current password is required'),
  newPassword: Yup.string()
    .min(8, 'Password must be at least 8 characters long')
    .matches(/[a-z]/, 'Password must contain at least one lowercase letter')
    .matches(/[A-Z]/, 'Password must contain at least one uppercase letter')
    .matches(/[0-9]/, 'Password must contain at least one number')
    .matches(/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?`~]/, 'Password must contain at least one special character')
    .required('New password is required'),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref('newPassword'), null], 'Passwords do not match')
    .required('Confirm new password is required')
});

// ==============================|| ADMIN PROFILE PAGE ||============================== //

const Profile = () => {
  const dispatch = useDispatch();
  const { user, updateProfile, changePassword } = useAuth();
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState('details');
  
  const [profileData, setProfileData] = useState({
    name: user?.name || '',
    email: user?.email || '',
    role: user?.role || 'PLATFORM_ADMIN',
    userType: user?.userType || 'ADMIN'
  });
  
  const [loading, setLoading] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  useEffect(() => {
    const fetchCurrentProfile = async () => {
      try {
        setLoading(true);
        const result = await getProfile();
        if (result.success) {
          setProfileData(result.data);
          // Sync to redux directly, avoid unnecessary PUT request
          dispatch(updateProfileSuccess(result.data));
        }
      } catch (err) {
        console.error('Failed to fetch profile details:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchCurrentProfile();
  }, [updateProfile]);

  const handleUpdateProfile = async (values, { setSubmitting }) => {
    console.log('handleUpdateProfile started with values:', values);
    try {
      console.log('Calling updateProfile...');
      const result = await updateProfile(values);
      console.log('updateProfile result:', result);
      if (result.success) {
        toast({
          title: "Profile Updated",
          description: "Your profile details have been saved successfully!",
        });
        setProfileData(prev => ({ ...prev, ...result.data }));
      } else {
        toast({
          title: "Update Failed",
          description: result.message || "Failed to update profile",
          variant: "destructive"
        });
      }
    } catch (err) {
      console.error('Failed to update profile:', err);
      toast({
        title: "Update Failed",
        description: err.response?.data?.message || err.message || "Failed to update profile",
        variant: "destructive"
      });
    } finally {
      console.log('Setting isSubmitting false');
      setSubmitting(false);
    }
  };

  const handleChangePassword = async (values, { setSubmitting, resetForm }) => {
    try {
      const result = await changePassword({
        currentPassword: values.currentPassword,
        newPassword: values.newPassword
      });
      
      if (result.success) {
        toast({
          title: "Password Changed",
          description: "Your password has been changed successfully!",
        });
        resetForm();
      } else {
        toast({
          title: "Failed to Change Password",
          description: result.message || "Could not change password",
          variant: "destructive"
        });
      }
    } catch (err) {
      console.error('Change password error:', err);
      toast({
        title: "Failed to Change Password",
        description: err.response?.data?.message || "Could not change password. Please check your credentials.",
        variant: "destructive"
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-6 animate-in fade-in-50 duration-300">
      {/* Page Header */}
      <div className="flex flex-col gap-1.5">
        <h1 className="text-2xl font-bold tracking-tight text-foreground">
          My Account
        </h1>
        <p className="text-sm text-muted-foreground">
          Manage your administrator profile details and security settings.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
        {/* Left Side: Summary Card */}
        <div className="md:col-span-4 space-y-6">
          <Card className="border-border shadow-md h-fit">
            <CardContent className="p-6 flex flex-col items-center text-center space-y-4">
              <Avatar className="h-24 w-24 border-2 border-primary/20 shadow-sm">
                <AvatarFallback className="bg-primary/10 text-primary text-2xl font-bold">
                  {profileData.name ? profileData.name.charAt(0).toUpperCase() : 'A'}
                </AvatarFallback>
              </Avatar>
              <div className="space-y-1">
                <h3 className="text-lg font-bold text-foreground capitalize">
                  {profileData.name || 'Admin User'}
                </h3>
                <p className="text-xs text-muted-foreground break-all">
                  {profileData.email}
                </p>
              </div>
              <div className="flex flex-wrap gap-2 justify-center">
                <Badge variant="outline" className="bg-primary/5 text-primary border-primary/25 font-semibold">
                  {profileData.role?.replace(/_/g, ' ')}
                </Badge>
                <Badge variant="outline" className="bg-muted text-muted-foreground border-border font-medium">
                  {profileData.userType}
                </Badge>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Right Side: Action Forms */}
        <div className="md:col-span-8">
          <Card className="border-border shadow-md">
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
              <div className="border-b px-6 pt-4">
                <TabsList className="bg-muted/40 w-fit flex gap-1">
                  <TabsTrigger value="details" className="px-4 py-2 font-medium">
                    Profile Details
                  </TabsTrigger>
                  <TabsTrigger value="security" className="px-4 py-2 font-medium">
                    Password & Security
                  </TabsTrigger>
                </TabsList>
              </div>

              {/* Profile Details Tab Content */}
              <TabsContent value="details" className="p-6 outline-none">
                <CardHeader className="p-0 mb-6">
                  <CardTitle className="text-xl">Profile Details</CardTitle>
                  <CardDescription>
                    Update your name and primary contact email.
                  </CardDescription>
                </CardHeader>
                
                {loading ? (
                  <div className="py-6 text-center text-muted-foreground">Loading details...</div>
                ) : (
                  <Formik
                    initialValues={{
                      name: profileData.name,
                      email: profileData.email
                    }}
                    validationSchema={profileSchema}
                    onSubmit={handleUpdateProfile}
                    enableReinitialize
                  >
                    {({ values, errors, touched, handleChange, handleBlur, isSubmitting }) => (
                      <Form className="space-y-4">
                        {/* Name Field */}
                        <div className="space-y-1.5">
                          <Label htmlFor="name">Full Name</Label>
                          <div className="relative">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-muted-foreground">
                              <User size={18} />
                            </div>
                            <Input
                              id="name"
                              name="name"
                              type="text"
                              value={values.name}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              placeholder="Full Name"
                              className={`pl-10 ${
                                touched.name && errors.name ? 'border-destructive focus-visible:ring-destructive' : ''
                              }`}
                            />
                          </div>
                          {touched.name && errors.name && (
                            <p className="text-xs text-destructive">{errors.name}</p>
                          )}
                        </div>

                        {/* Email Field */}
                        <div className="space-y-1.5">
                          <Label htmlFor="email">Email Address</Label>
                          <div className="relative">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-muted-foreground">
                              <Sms size={18} />
                            </div>
                            <Input
                              id="email"
                              name="email"
                              type="email"
                              value={values.email}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              placeholder="admin@optihhcp.com"
                              className={`pl-10 ${
                                touched.email && errors.email ? 'border-destructive focus-visible:ring-destructive' : ''
                              }`}
                            />
                          </div>
                          {touched.email && errors.email && (
                            <p className="text-xs text-destructive">{errors.email}</p>
                          )}
                        </div>

                        {/* Submit Details Button */}
                        <div className="flex justify-end pt-2">
                          <Button
                            type="submit"
                            disabled={isSubmitting}
                            className="bg-primary hover:bg-primary/90 text-primary-foreground font-semibold px-6 h-10 shadow-sm flex items-center gap-2"
                          >
                            {isSubmitting && (
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                            )}
                            Save Changes
                          </Button>
                        </div>
                      </Form>
                    )}
                  </Formik>
                )}
              </TabsContent>

              {/* Password & Security Tab Content */}
              <TabsContent value="security" className="p-6 outline-none">
                <CardHeader className="p-0 mb-6">
                  <CardTitle className="text-xl">Update Password</CardTitle>
                  <CardDescription>
                    Ensure your account is using a secure, complex password.
                  </CardDescription>
                </CardHeader>
                
                <Formik
                  initialValues={{
                    currentPassword: '',
                    newPassword: '',
                    confirmPassword: ''
                  }}
                  validationSchema={changePasswordSchema}
                  onSubmit={handleChangePassword}
                >
                  {({ values, errors, touched, handleChange, handleBlur, isSubmitting }) => (
                    <Form className="space-y-4">
                      {/* Current Password */}
                      <div className="space-y-1.5">
                        <Label htmlFor="currentPassword">Current Password</Label>
                        <div className="relative">
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-muted-foreground">
                            <Lock size={18} />
                          </div>
                          <Input
                            id="currentPassword"
                            name="currentPassword"
                            type={showCurrentPassword ? 'text' : 'password'}
                            value={values.currentPassword}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            placeholder="••••••••"
                            className={`pl-10 pr-10 ${
                              touched.currentPassword && errors.currentPassword ? 'border-destructive focus-visible:ring-destructive' : ''
                            }`}
                          />
                          <button
                            type="button"
                            onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                            className="absolute inset-y-0 right-0 pr-3 flex items-center text-muted-foreground hover:text-foreground focus:outline-none transition-colors"
                          >
                            {showCurrentPassword ? <EyeSlash size={18} /> : <Eye size={18} />}
                          </button>
                        </div>
                        {touched.currentPassword && errors.currentPassword && (
                          <p className="text-xs text-destructive">{errors.currentPassword}</p>
                        )}
                      </div>

                      {/* New Password */}
                      <div className="space-y-1.5">
                        <Label htmlFor="newPassword">New Password</Label>
                        <div className="relative">
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-muted-foreground">
                            <Lock size={18} />
                          </div>
                          <Input
                            id="newPassword"
                            name="newPassword"
                            type={showNewPassword ? 'text' : 'password'}
                            value={values.newPassword}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            placeholder="••••••••"
                            className={`pl-10 pr-10 ${
                              touched.newPassword && errors.newPassword ? 'border-destructive focus-visible:ring-destructive' : ''
                            }`}
                          />
                          <button
                            type="button"
                            onClick={() => setShowNewPassword(!showNewPassword)}
                            className="absolute inset-y-0 right-0 pr-3 flex items-center text-muted-foreground hover:text-foreground focus:outline-none transition-colors"
                          >
                            {showNewPassword ? <EyeSlash size={18} /> : <Eye size={18} />}
                          </button>
                        </div>
                        {touched.newPassword && errors.newPassword && (
                          <p className="text-xs text-destructive">{errors.newPassword}</p>
                        )}
                      </div>

                      {/* Confirm Password */}
                      <div className="space-y-1.5">
                        <Label htmlFor="confirmPassword">Confirm New Password</Label>
                        <div className="relative">
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-muted-foreground">
                            <Lock size={18} />
                          </div>
                          <Input
                            id="confirmPassword"
                            name="confirmPassword"
                            type={showConfirmPassword ? 'text' : 'password'}
                            value={values.confirmPassword}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            placeholder="••••••••"
                            className={`pl-10 pr-10 ${
                              touched.confirmPassword && errors.confirmPassword ? 'border-destructive focus-visible:ring-destructive' : ''
                            }`}
                          />
                          <button
                            type="button"
                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                            className="absolute inset-y-0 right-0 pr-3 flex items-center text-muted-foreground hover:text-foreground focus:outline-none transition-colors"
                          >
                            {showConfirmPassword ? <EyeSlash size={18} /> : <Eye size={18} />}
                          </button>
                        </div>
                        {touched.confirmPassword && errors.confirmPassword && (
                          <p className="text-xs text-destructive">{errors.confirmPassword}</p>
                        )}
                      </div>

                      {/* Password Rules Callout */}
                      <div className="bg-muted/40 border rounded-md p-4 flex gap-3 text-xs text-muted-foreground mt-4">
                        <ShieldSecurity size={20} className="shrink-0 text-primary" />
                        <div className="space-y-1">
                          <p className="font-semibold text-foreground">Password requirements:</p>
                          <ul className="list-disc pl-4 space-y-0.5">
                            <li>At least 8 characters long</li>
                            <li>At least one uppercase alphabet</li>
                            <li>At least one lowercase alphabet</li>
                            <li>At least one number</li>
                            <li>At least one special character (e.g. @, #, $, %)</li>
                          </ul>
                        </div>
                      </div>

                      {/* Submit Password Button */}
                      <div className="flex justify-end pt-2">
                        <Button
                          type="submit"
                          disabled={isSubmitting}
                          className="bg-primary hover:bg-primary/90 text-primary-foreground font-semibold px-6 h-10 shadow-sm flex items-center gap-2"
                        >
                          {isSubmitting && (
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                          )}
                          Change Password
                        </Button>
                      </div>
                    </Form>
                  )}
                </Formik>
              </TabsContent>
            </Tabs>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Profile;
